import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { News, Location } from "@/types";

// News types for different sections
export type NewsType =
  | "latest"
  | "hot"
  | "popular"
  | "mostsee"
  | "celebrity"
  | "viral";

// Simplified filters interface
interface NewsFilters {
  location: Location | null;
  category: string | null;
  search: string | null;
}

// UI State interface
interface UIState {
  isFilterMenuOpen: boolean;
  selectedNewsId: number | null;
  viewMode: "grid" | "list";
  isRefreshing: boolean;
}

// Main store interface - simplified to focus on UI state only
interface UnifiedNewsStore {
  // Core state
  filters: NewsFilters;
  ui: UIState;

  // Filter actions
  setLocation: (location: Location | null) => void;
  setCategory: (category: string | null) => void;
  setSearch: (search: string | null) => void;
  clearFilters: () => void;
  resetFilters: () => void;
  batchUpdateFilters: (filters: Partial<NewsFilters>) => void;

  // UI actions
  toggleFilterMenu: () => void;
  setSelectedNews: (newsId: number | null) => void;
  setViewMode: (mode: "grid" | "list") => void;
  setRefreshing: (isRefreshing: boolean) => void;
}

// Initial state
const initialFilters: NewsFilters = {
  location: null,
  category: null,
  search: null,
};

const initialUI: UIState = {
  isFilterMenuOpen: false,
  selectedNewsId: null,
  viewMode: "grid",
  isRefreshing: false,
};

// Create simplified store
export const useUnifiedNewsStore = create<UnifiedNewsStore>()(
  persist(
    immer((set) => ({
      // Initial state
      filters: initialFilters,
      ui: initialUI,

      // Filter actions
      setLocation: (location: Location | null) => {
        set((state) => {
          state.filters.location = location;
        });
      },

      setCategory: (category: string | null) => {
        set((state) => {
          state.filters.category = category;
        });
      },

      setSearch: (search: string | null) => {
        set((state) => {
          state.filters.search = search;
        });
      },

      clearFilters: () => {
        set((state) => {
          state.filters.location = null;
          state.filters.category = null;
          state.filters.search = null;
        });
      },

      resetFilters: () => {
        set((state) => {
          state.filters = { ...initialFilters };
        });
      },

      batchUpdateFilters: (filterUpdates: Partial<NewsFilters>) => {
        set((state) => {
          Object.assign(state.filters, filterUpdates);
        });
      },

      // UI actions
      toggleFilterMenu: () => {
        set((state) => {
          state.ui.isFilterMenuOpen = !state.ui.isFilterMenuOpen;
        });
      },

      setSelectedNews: (newsId: number | null) => {
        set((state) => {
          state.ui.selectedNewsId = newsId;
        });
      },

      setViewMode: (mode: "grid" | "list") => {
        set((state) => {
          state.ui.viewMode = mode;
        });
      },

      setRefreshing: (isRefreshing: boolean) => {
        set((state) => {
          state.ui.isRefreshing = isRefreshing;
        });
      },
    })),
    {
      name: "unified-news-store-v4",
      storage: createJSONStorage(() => localStorage),
      version: 4,

      // Only persist filters and view preferences
      partialize: (state) => ({
        filters: state.filters,
        ui: {
          viewMode: state.ui.viewMode,
        },
      }),

      // Simple migration
      migrate: (persistedState: any, version: number) => {
        if (version <= 3) {
          return {
            filters: persistedState.filters || initialFilters,
            ui: {
              viewMode: persistedState.ui?.viewMode || "grid",
            },
          };
        }
        return persistedState;
      },

      // Reset transient state on rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.ui = {
            ...initialUI,
            viewMode: state.ui?.viewMode || "grid",
          };
        }
      },
    },
  ),
);

// Selector hooks for better performance
export const useCurrentLocation = () =>
  useUnifiedNewsStore((state) => state.filters.location);

export const useCurrentCategory = () =>
  useUnifiedNewsStore((state) => state.filters.category);

export const useCurrentSearch = () =>
  useUnifiedNewsStore((state) => state.filters.search);

export const useViewMode = () =>
  useUnifiedNewsStore((state) => state.ui.viewMode);

export const useIsRefreshing = () =>
  useUnifiedNewsStore((state) => state.ui.isRefreshing);

// Default export
export default useUnifiedNewsStore;
